import { Link } from "@tanstack/react-router";
import { ChevronRight, FolderOpen, WifiOff } from "lucide-react";
import { useMemo } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useCaptureLocation, useCaptureProjects, useOnline } from "@/hooks/use-capture";
import { albumCountLabel, byDistance, distanceLabel } from "@/lib/capture";
import type { CaptureProject } from "@/schemas/uploads";

function ProjectRow({ project, km }: { project: CaptureProject; km: number | null }) {
  const details = [
    project.spNumber,
    km === null ? null : distanceLabel(km),
    albumCountLabel(project.albums.length),
  ].filter(Boolean);
  return (
    <li>
      <Link
        to="/capture/projects/$projectId"
        params={{ projectId: project.id }}
        data-testid="capture-project"
        className="flex min-h-16 items-center gap-3 rounded-lg border bg-card px-4 py-3 transition-colors active:bg-muted hover:bg-muted/50"
      >
        <span className="min-w-0 flex-1">
          <span className="block font-medium">{project.name}</span>
          <span className="block text-sm text-muted-foreground">{details.join(" · ")}</span>
        </span>
        <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
}

/** Every project, nearest first once the phone knows where it is. */
export function ProjectList() {
  const { data, isPending, isError, refetch } = useCaptureProjects();
  const { position } = useCaptureLocation();
  const online = useOnline();
  const sorted = useMemo(() => (data ? byDistance(data, position) : []), [data, position]);

  if (isPending) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((n) => (
          <Skeleton key={n} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }
  if (!data) {
    // A failed fetch with nothing saved: offline is the usual reason on site.
    return isError && !online ? (
      <EmptyState
        icon={WifiOff}
        title="You are offline and this list has not been saved on this phone yet. Open it once with signal."
        className="rounded-lg border border-dashed py-12"
      />
    ) : (
      <ErrorState message="Could not load the projects" onRetry={() => refetch()} />
    );
  }
  if (sorted.length === 0) {
    return (
      <EmptyState
        icon={FolderOpen}
        title="No projects yet"
        className="rounded-lg border border-dashed py-12"
      />
    );
  }

  return (
    <ul className="space-y-2" aria-label="Projects">
      {sorted.map(({ project, km }) => (
        <ProjectRow key={project.id} project={project} km={km} />
      ))}
    </ul>
  );
}
